/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Suspense, lazy, useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { GearIcon } from '@radix-ui/react-icons';
import {
  DataState, DataStateError, SegmentedControl, TableRowsSkeleton, Text, TriageBand,
  UnstyledButton,
} from '@/shared/ui';
import { useAgencyPermissions } from '@/features/agency';
import { ListingTable, type ListingColumn } from '@/shared/ui/components/basic/listing-table';
import { useTriStateSort } from '@/shared/hooks';
import { EmptyState } from '@/shared/ui/components/basic/EmptyState';
import { PageHeader } from '@features/property-management/components/PageHeader';
import { LeaseBoard } from '../components/LeaseBoard';
import { LeaseStateBadge } from '../components/LeaseStateBadge';
import {
  LeaseFilterPanel,
  applyLeaseFilters,
  buildLeaseFilterLabels,
  countLeaseFilters,
  leaseBrokerOptions,
  removeLeaseFilterByLabel,
  type LeaseFilters,
} from '../components/LeaseFilterPanel';
import { Pagination, Skeleton } from '@/shared/ui';
import { LEASE_STATES, useLeaseStateLabel, type LeaseState } from '../constants/lease-states';
import {
  LEASE_TRIAGE_IDS,
  LEASE_TRIAGE_TONES,
  applyLeaseTriage,
  countLeaseTriage,
  type LeaseTriageId,
} from '../constants/lease-triage';
import { useLeases } from '../hooks/useLeases';
import { formatAccrualPeriod } from '../utils/lease-detail-derivations';
import { useLeasePropertyLabels, type PropertyLabels } from '../hooks/useLeasePropertyLabels';
import type { Lease } from '../types/lease.types';
import { navIconFor } from '@layout/agency/agency-nav';

/** Rows per page in the list. */
const ROWS_PER_PAGE = 25;

/**
 * The two readings of one query.
 *
 * The table leads, unlike Atendimentos, where the board does: a lease is
 * read by its values and dates — first accrual period, responsible broker,
 * the columns one sorts on — while an attendance is read by where it sits
 * in the funnel. The board is here for the second question ("what is
 * running, what is suspended"), not as the primary way in.
 */
type LeaseView = 'board' | 'table';

const CreateLeaseDrawer = lazy(() => import('../components/create-lease'));

/**
 * A lease plus the fields the table sorts on.
 *
 * ListingTable compares `row[col.key]` with `<`, so anything sortable has
 * to be a scalar sitting directly on the row: `tenant` is an object and
 * `state` is an enum whose alphabetical order (`active`, `cancelled`,
 * `closed`, `draft`, ...) matches neither the lifecycle nor the labels on
 * screen.
 */
type LeaseRow = Lease & {
  id: string;
  sortTenantName: string;
  sortStateOrder: number;
  /** Null while the property's own lookup is still running. */
  propertyLabel: string | null;
};

/**
 * How a lease's property reads in the list.
 *
 * Null while its lookup is in flight, so the cell can show a placeholder:
 * the raw id used to fill that gap and read as a broken name. Once the
 * lookup is done, the raw id is the fallback only for a property that is
 * gone -- an id is a poor label, but an empty cell is worse.
 */
function propertyDisplay(
  lease: Lease,
  labels: PropertyLabels,
  pending: ReadonlySet<string>,
): string | null {
  if (pending.has(lease.property_id)) return null;
  return labels[lease.property_id] || lease.property_id || '—';
}

/**
 * Comparable form of a tenant name.
 *
 * Case-folded and accent-stripped because `<` compares code points, which
 * would file "Álvaro" after "Zeca" in a Portuguese list.
 */
function tenantSortName(lease: Lease): string {
  return (lease.tenant_name ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Lifecycle position of a state; unknown states sort last.
 *
 * @param state - The lease state to place.
 * @returns Its index in the lifecycle, or one past the end when unknown.
 */
function stateSortOrder(state: string): number {
  const index = LEASE_STATES.indexOf(state as (typeof LEASE_STATES)[number]);
  return index === -1 ? LEASE_STATES.length : index;
}

/**
 * Locações: the lease registry — search, filter panel, and the list.
 *
 * Registro e acompanhamento, never administration: no charge issuing, no
 * payout, no dunning (regra 12).
 *
 * @returns The leases list page.
 */
export function LeasesPage() {
  const { agency_id: agencyId = '' } = useParams<{ agency_id: string }>();
  const navigate = useNavigate();
  const { canManageLeaseSettings } = useAgencyPermissions();
  const { t, i18n } = useTranslation('features/leases');
  const [search, setSearch] = useState('');
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const stateLabel = useLeaseStateLabel();

  // The `state` query param predates the panel and keeps a filtered list
  // shareable by URL, so it seeds the panel once and is written back when
  // the state section changes. The panel is the source of truth after that.
  const [filters, setFilters] = useState<LeaseFilters>(() => {
    const fromUrl = searchParams.get('state');
    const isKnown = LEASE_STATES.includes(fromUrl as LeaseState);
    // `ending_within` arrives from the overview's "contratos vencendo"
    // tile. Read once, like `state`, and dropped from the URL as soon as
    // the panel changes anything, since the panel is the truth after that.
    const window = Number(searchParams.get('ending_within'));
    const endingWithinDays = Number.isFinite(window) && window > 0 ? window : undefined;
    return {
      ...(isKnown ? { states: [fromUrl as LeaseState] } : {}),
      ...(endingWithinDays === undefined ? {} : { endingWithinDays }),
    };
  });
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [triage, setTriage] = useState<LeaseTriageId | null>(null);
  const view: LeaseView = searchParams.get('view') === 'board' ? 'board' : 'table';

  const setView = useCallback((next: string) => {
    setSearchParams((params) => {
      if (next === 'board') params.set('view', next);
      else params.delete('view');
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Server-side: the registry does not carry payment state, so this one
  // narrowing cannot be derived from the rows already loaded.
  const wantsOverdue = searchParams.get('overdue') === 'true';
  const { isLoading, isError, leases, refresh } = useLeases(agencyId, wantsOverdue);

  const syncStateToUrl = useCallback((next: LeaseFilters) => {
    setSearchParams((params) => {
      // Only a lone state survives the round trip; a multi-select has no
      // single-value representation, so the param is dropped rather than
      // encoding a half-truth the next visitor would load as the whole set.
      if (next.states?.length === 1) params.set('state', next.states[0]);
      else params.delete('state');
      if (next.endingWithinDays === undefined) params.delete('ending_within');
      else params.set('ending_within', String(next.endingWithinDays));
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const applyFilters = useCallback((next: LeaseFilters) => {
    setFilters(next);
    syncStateToUrl(next);
  }, [syncStateToUrl]);

  const { labels: propertyLabels, pending: pendingProperties } = useLeasePropertyLabels(
    agencyId,
    useMemo(() => leases.map((l) => l.property_id), [leases]),
  );
  const filtered = useMemo(() => {
    const byFilters = applyLeaseFilters(leases, filters);
    const query = search.trim().toLowerCase();
    if (!query) return byFilters;
    // Matches the address the broker can actually see, not only the opaque
    // id: the placeholder promises "inquilino ou imóvel", and before this
    // the imóvel half could only be satisfied by typing a raw id.
    return byFilters.filter((l) =>
      (l.tenant_name ?? '').toLowerCase().includes(query)
      || l.property_id.toLowerCase().includes(query)
      || (propertyLabels[l.property_id] ?? '').toLowerCase().includes(query));
  }, [leases, filters, search, propertyLabels]);

  // Counted over the whole registry, never over `filtered`: the band answers
  // "what needs me today", and a count that shrank as the broker typed in
  // the search box would be answering "…among what you are looking at",
  // which is a different and far less useful question.
  const triageCounts = useMemo(() => countLeaseTriage(leases), [leases]);

  // Applied after the filters and the search, so the two narrowings compose
  // and the board and the table always show the same set.
  const visible = useMemo(
    () => applyLeaseTriage(filtered, triage),
    [filtered, triage],
  );

  // `key` is also the sort field: the shared comparator reads `row[col.key]`
  // (or a `sortAccessor`), so a key that names no field silently sorts
  // nothing. Hence `sortTenantName` and `sortStateOrder` are projected onto
  // each row rather than sorted through a display-only key.
  const columns: ListingColumn<LeaseRow>[] = [
    {
      key: 'sortTenantName',
      label: t('columns.tenant'),
      sortable: true,
      render: (l) => <Text component="span">{l.tenant_name || '—'}</Text>,
    },
    {
      key: 'propertyLabel',
      label: t('columns.property'),
      sortable: true,
      render: (l) => (l.propertyLabel === null
        ? <Skeleton height={14} width="70%" radius="var(--radius-sm)" data-testid="lease-property-pending" />
        : <Text component="span">{l.propertyLabel}</Text>),
    },
    {
      key: 'sortStateOrder',
      label: t('columns.state'),
      sortable: true,
      sortType: 'number',
      render: (l) => <LeaseStateBadge state={l.state} />,
    },
    {
      // Sorted on the raw ISO period, displayed as a named month: the
      // stored `YYYY-MM` already orders chronologically as text.
      key: 'first_accrual_period',
      label: t('columns.firstAccrualPeriod'),
      sortable: true,
      render: (l) => (
        <Text component="span">
          {l.first_accrual_period
            ? formatAccrualPeriod(l.first_accrual_period, i18n.language)
            : '—'}
        </Text>
      ),
    },
    {
      key: 'responsible_broker_email',
      label: t('columns.broker'),
      sortable: true,
      render: (l) => <Text component="span">{l.responsible_broker_email || '—'}</Text>,
    },
  ];

  // Sort the FULL 'visible' set through the shared engine BEFORE paginating,
  // so the order spans every page instead of the 25 rows the old code sliced
  // first (the page-scoped sort bug). The projection carries the same hidden
  // sort scalars the columns key onto.
  const allRows: LeaseRow[] = useMemo(
    () => visible.map((l) => ({
      ...l,
      id: l.id,
      sortTenantName: tenantSortName(l),
      sortStateOrder: stateSortOrder(l.state),
      propertyLabel: propertyDisplay(l, propertyLabels, pendingProperties),
    })),
    [visible, propertyLabels, pendingProperties],
  );

  const { sortKey, sortDir, toggleSort, sortData } = useTriStateSort<LeaseRow>(columns);
  const sortedRows = useMemo(() => sortData(allRows), [sortData, allRows]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / ROWS_PER_PAGE));

  // The page lives in the URL because the shared Pagination puts it there,
  // which also makes a deep link land on the row the broker was looking at.
  // Clamped on read: a narrowed result set can leave the current page past
  // the end, and an empty table after filtering reads as "no results".
  const currentPage = Math.min(
    Math.max(1, Number(searchParams.get('page') ?? '1') || 1),
    pageCount,
  );
  const pageRows = useMemo(
    () => sortedRows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE),
    [sortedRows, currentPage],
  );

  const brokerOptions = useMemo(() => leaseBrokerOptions(leases), [leases]);
  const filterCount = countLeaseFilters(filters) + (wantsOverdue ? 1 : 0);
  // The overdue narrowing lives in the URL rather than in the panel -- it
  // is a server-side query, not a predicate over the loaded rows -- but it
  // still has to show up as a chip, or the list silently hides contracts.
  const activeFilterLabels = useMemo(
    () => [
      ...buildLeaseFilterLabels(filters, t, stateLabel),
      ...(wantsOverdue ? [t('filters.overdue')] : []),
    ],
    [filters, t, stateLabel, wantsOverdue],
  );

  const clearOverdue = useCallback(() => {
    setSearchParams((params) => {
      params.delete('overdue');
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const hasActiveQuery = Boolean(search.trim()) || filterCount > 0;

  return (
    <div className="flex flex-1 min-h-0 w-full flex-col overflow-hidden bg-[var(--surface-secondary)] font-[family-name:var(--font-family)]">
      <div className="flex flex-1 min-h-0 w-full flex-col gap-[var(--space-4)] overflow-auto p-[var(--space-5)] px-[var(--space-6)]">
        <PageHeader
          title={t('pageTitle')}
          icon={navIconFor('leases', 20)}
          ctaLabel={t('create.cta')}
          onCtaClick={() => setCreateOpen(true)}
          searchPlaceholder={t('searchPlaceholder')}
          searchValue={search}
          onSearchChange={setSearch}
          onFiltersToggle={() => setFiltersOpen((open) => !open)}
          activeFilters={activeFilterLabels}
          onRemoveFilter={(label) => {
            if (label === t('filters.overdue')) clearOverdue();
            else applyFilters(removeLeaseFilterByLabel(filters, label, t, stateLabel));
          }}
          onClearAllFilters={() => { clearOverdue(); applyFilters({}); }}
          filterCount={filterCount}
          introLabel={t('intro.label')}
          introBody={t('intro.body')}
          introTestIdPrefix="leases"
          trailingAction={canManageLeaseSettings ? (
            <UnstyledButton
              type="button"
              data-testid="leases-settings-shortcut"
              onClick={() => navigate(`/app/agencies/${agencyId}/settings?section=locacoes`)}
              style={settingsShortcutStyle}
            >
              <GearIcon width={14} height={14} />
              {t('settingsShortcut')}
            </UnstyledButton>
          ) : undefined}
        />

        <DataState
          isLoading={isLoading}
          isError={isError}
          isEmpty={false}
          loadingFallback={<TableRowsSkeleton rows={6} />}
          errorState={<DataStateError message={t('loadError')} onRetry={refresh} />}
        >
          {/* Above the count because it decides what is being counted.
              Honest here in a way it could not be on Atendimentos: the
              page holds the whole registry, so these are totals. */}
          <TriageBand
            testId="lease-triage"
            groups={LEASE_TRIAGE_IDS.map((id) => ({
              id,
              count: triageCounts[id],
              label: t(`triage.${id}.label`),
              hint: t(`triage.${id}.hint`),
              tone: LEASE_TRIAGE_TONES[id],
              icon: <KeyRound width={17} height={17} />,
            }))}
            selected={triage}
            onSelect={(id) => setTriage(id as LeaseTriageId | null)}
          />

          {/* The count qualifies whichever view is on screen, so it shares
              the toggle's row rather than sitting on a line of its own. */}
          <div className="flex flex-wrap items-center justify-between gap-(--space-3)">
            <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
              {t('list.resultsCount', { count: visible.length })}
            </Text>
            <SegmentedControl
              value={view}
              onChange={setView}
              items={[
                { value: 'board', label: t('views.board') },
                { value: 'table', label: t('views.table') },
              ]}
              size="sm"
              testId="leases-view-toggle"
            />
          </div>

          {visible.length === 0 ? (
            <EmptyState
              icon={navIconFor('leases', 28)}
              title={hasActiveQuery ? t('list.noResults.title') : t('list.empty.title')}
              description={hasActiveQuery ? t('list.noResults.description') : t('list.empty.description')}
              // Only on the genuinely-empty state: when a search returned
              // nothing, the lease the broker wants may well already exist.
              action={hasActiveQuery ? undefined : {
                label: t('create.cta'),
                onClick: () => setCreateOpen(true),
              }}
            />
          ) : view === 'board' ? (
            <LeaseBoard
              agencyId={agencyId}
              leases={visible}
              tenantNameOf={(l) => l.tenant_name ?? ''}
              propertyLabelOf={(l) => propertyDisplay(l, propertyLabels, pendingProperties)}
            />
          ) : (
            <div className="overflow-auto rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-sm)]">
              <ListingTable
                data={pageRows}
                columns={columns}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={toggleSort}
                onRowClick={(l) => navigate(`/app/agencies/${agencyId}/leases/${l.id}`)}
                testId="leases-listing-table"
              />
            </div>
          )}

          {/* Only once there is a second page, and only under the table: the
              board is unpaged, so a control there would page nothing. */}
          {view === 'table' && pageCount > 1 && (
            <div className="flex justify-center py-[var(--space-2)]">
              <Pagination numPages={pageCount} />
            </div>
          )}
        </DataState>
      </div>

      <LeaseFilterPanel
        open={isFiltersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={applyFilters}
        onClear={() => applyFilters({})}
        initialFilters={filters}
        brokers={brokerOptions}
      />

      {isCreateOpen && (
        <Suspense fallback={null}>
          <CreateLeaseDrawer
            agencyId={agencyId}
            open={isCreateOpen}
            onClose={() => setCreateOpen(false)}
            // Straight to the draft: it still has to be activated, and
            // activation is where the backend names anything missing.
            onCreated={(leaseId) => navigate(`/app/agencies/${agencyId}/leases/${leaseId}`)}
          />
        </Suspense>
      )}
    </div>
  );
}

export default LeasesPage;

/**
 * Matches the filters toggle beside it — same height, border and muted text,
 * so the two read as one pair of secondary controls rather than the settings
 * shortcut competing with the filter.
 */
const settingsShortcutStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--text-body-sm)',
  fontWeight: 'var(--font-medium)' as unknown as number,
  background: 'transparent',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-medium)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
  whiteSpace: 'nowrap',
};
